import { defineAuthProvider } from '@zg-rentals/auth-react/legacy';
import { createAuthPredicates } from '@zg-rentals/auth-comet/mfa';
import { getClient } from '@zg-rentals/http-client';
import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/hooks';
import type { RootState } from '../../../store/types';
import UserModel from '@common/models/User';
import UserActions from '@common/actions/UserActions';

type RenterUser = {
  loggedIn: boolean;
  hasStrongAuthentication: boolean;
  isInitialMultiFactorAuthenticationRequired: boolean;
  userToken: string;
  email: string;
  impersonatorEmail: string;
  impersonatorUserToken: string;
};

type RenterAuthProps = {
  brand: string;
  initialUser: RenterUser;
};

const { hasInitiatedAuth: hasRenterInitiatedAuth, hasAuth: hasRenterAuth } = createAuthPredicates<RenterUser>(
  (user) => ({
    ...user,
    isLoggedIn: user.loggedIn,
  }),
);

export { hasRenterInitiatedAuth, hasRenterAuth };

export const RenterAuth = defineAuthProvider<RenterAuthProps, RenterUser>({
  queryKey: ['rent-payment-web', 'renter-auth'],
  queryOptions: ({ initialUser }) => ({
    queryFn: () =>
      getClient()
        .get('/renter-hub/payments/proxy/renter-hub-api/api/v1/users/get', {
          searchParams: { pageWithSensitiveInformation: true, includeSummary: true },
        })
        .json<{ success: boolean; response?: Parameters<typeof UserModel>[0] }>()
        .then(({ success, response }) => {
          if (!success || !response) throw new Error('getUser failed');
          return UserModel(response);
        }),
    enabled: false,
    initialData: initialUser,
  }),
  isAuthorized: hasRenterInitiatedAuth,
});

export const { useAuth: useRenterAuth } = RenterAuth;

function RenterAuthSyncer() {
  const dispatch = useAppDispatch();
  const { user } = useRenterAuth();
  useEffect(() => {
    if (user) {
      dispatch(UserActions.updateUserInfo(user));
    }
  }, [user, dispatch]);
  return null;
}

export const RenterAuthProvider = ({ children }: { children: ReactNode }) => {
  const user = useSelector((state: RootState) => state.user);
  const brand = useSelector((state: RootState) => state.app.brand);
  const initialUser: RenterUser = useMemo(() => UserModel(user), [user]);

  return (
    <RenterAuth.Provider brand={brand} initialUser={initialUser}>
      <RenterAuthSyncer />
      {children}
    </RenterAuth.Provider>
  );
};
